import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@izicvs/ui";
import { t } from "@lingui/macro";
import { ArrowSquareOut, CheckCircle, Copy, FilePdf, Info } from "@phosphor-icons/react";
import { useEffect, useState } from "react";

import { toast } from "@/client/hooks/use-toast";
import { useCreateApplication } from "@/client/services/applications";
import type { PreparedApplication } from "@/client/services/jobs";
import { downloadPdf, usePrintResume } from "@/client/services/resume/print";

export const ApplicationDialog = ({
  application,
  jobId,
  resumeId,
  open,
  onOpenChange,
}: {
  application: PreparedApplication | null;
  jobId: string | null;
  resumeId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { printResume, loading: printing } = usePrintResume();
  const { create: track } = useCreateApplication();
  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    if (application) {
      setValues(Object.fromEntries(application.fields.map((field) => [field.key, field.value])));
    }
  }, [application]);

  const onCopy = () => {
    if (!application) return;
    const text = application.fields
      .filter((field) => field.type !== "file")
      .map((field) => `${field.label}: ${values[field.key] ?? ""}`)
      .join("\n\n");
    void navigator.clipboard.writeText(text);
    toast({ variant: "success", title: t`Copied to clipboard` });
  };

  const onDownloadCv = async () => {
    if (!resumeId) return;
    try {
      const { url } = await printResume({ id: resumeId });
      downloadPdf(url, "cv.pdf");
    } catch (error) {
      toast({ variant: "error", title: (error as Error).message });
    }
  };

  const onMarkApplied = async () => {
    if (!application) return;
    try {
      await track({
        jobTitle: application.jobTitle,
        company: application.company,
        url: application.url || undefined,
        jobId: jobId ?? undefined,
        resumeId: resumeId ?? undefined,
        status: "applied",
      });
      toast({ variant: "success", title: t`Marked as applied` });
      onOpenChange(false);
    } catch (error) {
      toast({ variant: "error", title: (error as Error).message });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t`Prepared application`}</DialogTitle>
          <DialogDescription>
            {application ? `${application.jobTitle} · ${application.company}` : ""}
          </DialogDescription>
        </DialogHeader>

        {application && !application.supported ? (
          <p className="text-sm opacity-70">
            {t`Auto-fill isn't available for this source yet. Open the offer to apply.`}
          </p>
        ) : (
          <div className="grid gap-y-4">
            <div className="flex items-start gap-x-2 rounded-lg border border-info/40 bg-info/10 p-3 text-xs">
              <Info weight="fill" className="mt-0.5 shrink-0 text-info" />
              <span>
                {t`Review and edit before applying. Automatic submission is coming next. For now, use these answers on the offer.`}
              </span>
            </div>

            {(application?.fields ?? [])
              .filter((field) => field.type !== "file")
              .map((field) => (
                <div key={field.key} className="space-y-1.5">
                  <Label className="flex items-center gap-x-2">
                    {field.label}
                    {field.required && (
                      <Badge variant="secondary" className="text-[10px]">
                        {t`required`}
                      </Badge>
                    )}
                  </Label>
                  {field.type === "textarea" ? (
                    <textarea
                      value={values[field.key] ?? ""}
                      className="min-h-[80px] w-full rounded-md border bg-transparent px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                      onChange={(event) => {
                        setValues((current) => ({ ...current, [field.key]: event.target.value }));
                      }}
                    />
                  ) : (
                    <Input
                      value={values[field.key] ?? ""}
                      onChange={(event) => {
                        setValues((current) => ({ ...current, [field.key]: event.target.value }));
                      }}
                    />
                  )}
                  {field.options && field.options.length > 0 && (
                    <p className="text-xs opacity-50">
                      {t`Options`}: {field.options.join(" · ")}
                    </p>
                  )}
                </div>
              ))}

            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={onCopy}>
                <Copy className="mr-2" />
                {t`Copy answers`}
              </Button>
              {resumeId && (
                <Button variant="secondary" disabled={printing} onClick={onDownloadCv}>
                  <FilePdf className="mr-2" />
                  {t`Download CV (PDF)`}
                </Button>
              )}
              {application?.url && (
                <Button asChild>
                  <a href={application.url} target="_blank" rel="noreferrer noopener">
                    <ArrowSquareOut className="mr-2" />
                    {t`Open offer to apply`}
                  </a>
                </Button>
              )}
              <Button variant="ghost" onClick={onMarkApplied}>
                <CheckCircle weight="fill" className="mr-2 text-success" />
                {t`I applied`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
